/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1902149638 — Точка входу — ScanQrCode */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { generatePath, useNavigate } from 'react-router-dom';

import { FEEDBACK_ROUTES, getFeedbackDataRequest } from '../../modules/feedback';
import { useFeedbackStore } from '../../modules/feedback/hooks/useFeedbackStore';
import { WaitSource } from '../../modules/event-log';
import { ORDER_ROUTES } from '../../modules/order';
import { getOrderInfo } from '../../modules/order/api/order.api';
import { PAYMENT_ROUTES } from '../../modules/payment';
import { SERVICE_ROUTES } from '../../modules/service';
import { STATUS_ROUTES } from '../../modules/status';
import { useAppStore } from '../../shared/hooks/useAppStore';
import { debug, getVmDetails, screenLog } from '../../shared/lib/general';
import { clearAllSavedStates, storeOrderState } from '../../shared/lib/stateStorage';
import { VendingMachine } from '../../shared/types';
import { ContentBox, Input, InputValidationMsg, PrimaryButton, ScreenTitle, ScreenWrapper } from '../../shared/ui';
import { checkQr } from './checkQr';

import styles from './ScanQrCode.module.css';

const ScanQrCode = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [urlParams] = useState(new URLSearchParams(window.location.search));
    const [urlQr] = useState(urlParams.get('qr'));
    const [urlOrder] = useState(urlParams.get('oid'));
    const [urlService] = useState(urlParams.get('service'));

    const vm = useAppStore((s) => s.vm);
    const setVm = useAppStore((s) => s.setVm);
    const qrCodeStr = useAppStore((s) => s.qrCode);
    const setQrCodeStr = useAppStore((s) => s.setQrCode);
    const order = useAppStore((s) => s.order);
    const setOrder = useAppStore((s) => s.setOrder);

    const [error, setError] = useState({ qrValidation: '' }); // Feedback creation on QR scan
    const setFeedback = useFeedbackStore((s) => s.setFeedback);

    // qrCheck - unsupported VM
    const handleUnsupportedVM = (qr: string = qrCodeStr) => {
        debug('handleUnsupportedVM:' + qr, 'warn');
        clearAllSavedStates();
        navigate(generatePath(STATUS_ROUTES.notSupported, { id: qr }), {
            replace: true,
        });
    };
    // qrCheck - not connected VM
    const handleDisconnectedVM = (qr: string = qrCodeStr) => {
        navigate(generatePath(STATUS_ROUTES.notConnected, { id: qr }), {
            replace: true,
        });
    };
    const qrCheckFailHandler = () => {
        clearAllSavedStates();
        setError({ qrValidation: 'Invalid code' });
    };
    // qrCheck - success
    const qrCheckSuccessHandler = (vmData: VendingMachine) => {
        setVm(vmData);
        navigate(generatePath(ORDER_ROUTES.connecting, { id: vmData.qr }), {
            replace: true,
        });
    };
    // servicePage
    const setData = (vmData: VendingMachine) => {
        debug('setData', 'data', vmData);

        setVm(vmData);
    };

    const goToMainService = (qr: string = qrCodeStr) => {
        navigate(generatePath(SERVICE_ROUTES.main, { id: qr }), { replace: true });
    };
    const goToRobotPage = (qr: string = qrCodeStr) => {
        navigate(generatePath(SERVICE_ROUTES.robot, { id: qr }), { replace: true });
    };
    const goToFeedbackPage = (qr: string = qrCodeStr, vm?: number) => {
        vm && getFeedbackData(vm);
        navigate(generatePath(FEEDBACK_ROUTES.smile, { id: qr }), {
            replace: true,
        });
    };

    async function getFeedbackData(vmId: number): Promise<void> {
        try {
            const result = await getFeedbackDataRequest(vmId, {
                wallet_user_id: order.user_id,
                transaction_id: order.id,
            });
            setFeedback(result);
        } catch (error) {
            console.error('Request failed:', error);
        }
    }

    useEffect(() => {
        screenLog('Scan QR');
    }, []);

    // existing logic
    // if (urlOrder) and !order (order.id?) then set as processing payment and fetch order
    // else if urlQr and it differs from qr in the store - then set store qr and check it
    useEffect(() => {
        debug(
            `ScanQrCode useEffect 2 \nurlQr:${urlQr}\n vmQr: ${vm.qr} \n urlOrder: ${urlOrder}`,
            'data',
        );

        // order exists
        if (urlOrder && !order.id) {
            debug('order exists, processing', 'data', order, urlOrder);
            // 1. get order details
            // 2. set order
            // 3. navigate waiting screen
            (async () => {
                try {
                    if (!urlOrder || urlOrder === '' || urlOrder === null) {
                        debug('no orderId', 'warn');
                        throw new Error('Order ID is not set');
                    }
                    const orderData = await getOrderInfo(urlOrder);
                    debug('orderInfo', 'data', orderData);
                    setOrder(orderData);
                    storeOrderState(orderData);
                    navigate(
                        generatePath(PAYMENT_ROUTES.wait, {
                            id: String(orderData.id),
                        }),
                        { replace: true, state: { waitSource: 'recovery' satisfies WaitSource } },
                    );
                } catch (e) {
                    debug('Error loading order info', 'error');
                    navigate(
                        generatePath(ORDER_ROUTES.failed, {
                            id: String(order.id),
                        }),
                        { replace: true },
                    );
                }
            })();
        } else if (urlQr && urlQr !== vm.qr) {
            debug('new QR', 'data');
            setQrCodeStr(urlQr);

            if (urlService) {
                debug(`urlService - ${urlService}`, 'data');
            }
            checkQr(
                {
                    qr: urlQr,
                    onSuccess: qrCheckSuccessHandler,
                    onFailure: qrCheckFailHandler,
                    onNotSupported: handleUnsupportedVM,
                    onNotConnected: handleDisconnectedVM,
                    onFiscalService: goToRobotPage,
                    onMainService: goToMainService,
                    onsetVm: setData,
                    onFeedbacks: goToFeedbackPage,
                },
                { service: Boolean(urlService) },
            );
        } else {
            debug('346 no check in ScanQr', 'error');
            debug('VM', 'data', vm);
            debug('is service', 'data', urlService);
            const { qr, isConnected, isAllowed } = getVmDetails(vm);
            if (
                urlQr &&
                urlQr === qr &&
                isConnected &&
                isAllowed &&
                !urlService
            ) {
                // we are on the same VM
                qrCheckSuccessHandler(vm);
            }
        }
    }, [vm, order]);

    const submitButtonHandler = () => {
        if (!qrCodeStr || qrCodeStr?.length === 0) {
            setError({ qrValidation: t('Enter code') });
        } else {
            setError({ qrValidation: '' });
            checkQr({
                qr: qrCodeStr,
                onSuccess: qrCheckSuccessHandler,
                onFailure: qrCheckFailHandler,
                onNotSupported: handleUnsupportedVM,
                onNotConnected: handleDisconnectedVM,
                onFiscalService: goToRobotPage,
                onMainService: goToMainService,
                onsetVm: setData,
                onFeedbacks: goToFeedbackPage,
            });
        }
    };
    const formSubmitHandler = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        submitButtonHandler();
    };

    return (
        <ScreenWrapper className={styles.centeredScreen}>
            <ScreenTitle>{t('Scan QR')}</ScreenTitle>
            <ContentBox>
                <form onSubmit={formSubmitHandler}>
                    <Input
                        type="text"
                        placeholder={t<string>('Enter QR')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQrCodeStr(e.target.value)}
                        value={qrCodeStr}
                    />
                    <InputValidationMsg>
                        {t(error?.qrValidation ?? 'error')}
                    </InputValidationMsg>
                    <PrimaryButton type="button" onClick={submitButtonHandler}>
                        {t('OK')}
                    </PrimaryButton>
                </form>
            </ContentBox>
        </ScreenWrapper>
    );
};

export { ScanQrCode };
